/**
 * Service to check for application updates and notify users
 * 
 * This service fetches a version identifier (e.g., build timestamp) from a
 * `version.json` file on the server. It compares this with the version last
 * seen by the user (stored in localStorage). If they differ, it signals that
 * an update is available.
 */

// Keys for storing the last seen versions in localStorage
const LAST_SEEN_DATA_STATE_VERSION_KEY = 'mr-architect-data-state-version';
const LAST_SEEN_FILES_VERSION_KEY = 'mr-architect-files-version';
const VERSION_JSON_PATH = '/version.json'; // Path to the version file

export interface AppVersions { // Added export keyword
  dataStateVersion: string;
  filesVersion: string;
}

class UpdateService {
  private static instance: UpdateService;
  private lastSeenDataStateVersion: string | null = null;
  private lastSeenFilesVersion: string | null = null;
  private checkInterval: number | null = null;
  private onUpdateAvailable: ((newVersions: AppVersions) => void) | null = null;

  // For DEV mode: helps control notifications during active development
  private devModeSimulatedUpdate = false;

  private constructor() {
    if (typeof window !== 'undefined') {
      this.lastSeenDataStateVersion = localStorage.getItem(LAST_SEEN_DATA_STATE_VERSION_KEY);
      this.lastSeenFilesVersion = localStorage.getItem(LAST_SEEN_FILES_VERSION_KEY);
    }
  }

  static getInstance(): UpdateService {
    if (!UpdateService.instance) {
      UpdateService.instance = new UpdateService();
    }
    return UpdateService.instance;
  }

  private async fetchLatestVersions(): Promise<AppVersions | null> {
    try {
      const response = await fetch(`${VERSION_JSON_PATH}?t=${Date.now()}`);
      if (!response.ok) {
        console.error(`Failed to fetch ${VERSION_JSON_PATH}: ${response.statusText}`);
        return null;
      }
      const data = await response.json();
      if (data && data.dataStateVersion && data.filesVersion) {
        return {
          dataStateVersion: String(data.dataStateVersion),
          filesVersion: String(data.filesVersion),
        };
      }
      console.error(`Invalid version data received from ${VERSION_JSON_PATH}:`, data);
      return null;
    } catch (error) {
      console.error(`Error fetching or parsing ${VERSION_JSON_PATH}:`, error);
      return null;
    }
  }

  /**
   * Check if a new version of the application is available by fetching version.json
   */
  async checkForUpdates(): Promise<boolean> {
    if (typeof window === 'undefined') return false;

    const fetchedVersions = await this.fetchLatestVersions();
    if (!fetchedVersions) {
      return false; // Failed to get current versions from server
    }

    // Handle first visit or cleared localStorage
    if (!this.lastSeenDataStateVersion || !this.lastSeenFilesVersion) {
      localStorage.setItem(LAST_SEEN_DATA_STATE_VERSION_KEY, fetchedVersions.dataStateVersion);
      localStorage.setItem(LAST_SEEN_FILES_VERSION_KEY, fetchedVersions.filesVersion);
      this.lastSeenDataStateVersion = fetchedVersions.dataStateVersion;
      this.lastSeenFilesVersion = fetchedVersions.filesVersion;
      console.log('UpdateService: First visit or localStorage cleared. Stored current versions:', fetchedVersions);
      return false; // No update on first visit, just record the versions
    }

    const dataStateVersionChanged = this.lastSeenDataStateVersion !== fetchedVersions.dataStateVersion;
    const filesVersionChanged = this.lastSeenFilesVersion !== fetchedVersions.filesVersion;
    const versionsDiffer = dataStateVersionChanged || filesVersionChanged;

    if (versionsDiffer) {
      if (import.meta.env.DEV && !this.devModeSimulatedUpdate) {
        console.log(
          `UpdateService (DEV): Version mismatch detected.`,
          `Last Seen: { data: ${this.lastSeenDataStateVersion}, files: ${this.lastSeenFilesVersion} }`,
          `Fetched: { data: ${fetchedVersions.dataStateVersion}, files: ${fetchedVersions.filesVersion} }. Not simulated. No UI notification.`
        );
        return false;
      }

      if (this.onUpdateAvailable) {
        this.onUpdateAvailable(fetchedVersions);
      }
      return true;
    } else {
      // Versions match
      if (import.meta.env.DEV) {
        this.devModeSimulatedUpdate = false; // Reset simulation flag
      }
      return false;
    }
  }

  /**
   * Start periodically checking for updates
   * @param intervalMs How often to check for updates (in milliseconds)
   */
  startPeriodicChecks(intervalMs: number = 60000): void {
    // This can run in DEV too; notifications are controlled by devModeSimulatedUpdate
    this.stopPeriodicChecks();
    
    this.checkInterval = window.setInterval(() => {
      this.checkForUpdates();
    }, intervalMs);
  }

  /**
   * Stop periodically checking for updates
   */
  stopPeriodicChecks(): void {
    if (this.checkInterval !== null) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  /**
   * Set a callback to be called when an update is available
   * @param callback The function to call when an update is available
   */
  onUpdate(callback: (newVersions: AppVersions) => void): void {
    this.onUpdateAvailable = callback;
  }

  /**
   * Apply the update by reloading the page.
   * Stores the new versions (from version.json) as the last seen versions.
   * @param newVersions The new versions to be applied.
   */
  applyUpdate(newVersions: AppVersions): void {
    if (typeof window === 'undefined') return;

    localStorage.setItem(LAST_SEEN_DATA_STATE_VERSION_KEY, newVersions.dataStateVersion);
    localStorage.setItem(LAST_SEEN_FILES_VERSION_KEY, newVersions.filesVersion);
    this.lastSeenDataStateVersion = newVersions.dataStateVersion;
    this.lastSeenFilesVersion = newVersions.filesVersion;

    if (import.meta.env.DEV) {
      this.devModeSimulatedUpdate = false; // Reset simulation flag after applying
    }

    window.location.reload();
  }

  /**
   * For testing in development mode: simulate a new build being deployed.
   * This sets the last seen versions to old values and triggers a check.
   */
  simulateNewBuild(): void {
    if (typeof window !== 'undefined' && import.meta.env.DEV) {
      console.log('UpdateService (DEV): Simulating new build...');
      const simulatedOldDataVersion = 'simulated-old-data-state-version';
      const simulatedOldFilesVersion = 'simulated-old-files-version';

      localStorage.setItem(LAST_SEEN_DATA_STATE_VERSION_KEY, simulatedOldDataVersion);
      localStorage.setItem(LAST_SEEN_FILES_VERSION_KEY, simulatedOldFilesVersion);
      this.lastSeenDataStateVersion = simulatedOldDataVersion;
      this.lastSeenFilesVersion = simulatedOldFilesVersion;
      this.devModeSimulatedUpdate = true; // Indicate that a simulation has occurred

      // Trigger an update check
      this.checkForUpdates().then(hasUpdate => {
        console.log('UpdateService (DEV): Simulated check complete. Update available:', hasUpdate);
      });
    } else {
      console.warn('simulateNewBuild is only available in development mode.');
    }
  }
}

export const updateService = UpdateService.getInstance();

// Expose the service to the window for debugging in development mode
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as any).updateService = updateService;
}
